<template>
    <el-form
        ref="formRef"
        label-position="top"
        class="contact-form"
        :model="form"
        :rules="rules"
    >
        <div class="contact-form-row">
            <el-form-item label="First name" prop="first_name">
                <el-input :value="form.first_name" @input="update('first_name', $event)" />
            </el-form-item>
            <el-form-item label="Last name" prop="last_name">
                <el-input :value="form.last_name" @input="update('last_name', $event)" />
            </el-form-item>
        </div>
        <el-form-item label="Email" prop="emailadres">
            <el-input type="email" :value="form.emailadres" @input="update('emailadres', $event)" />
        </el-form-item>
        <div class="contact-form-row">
            <el-form-item label="Mobile" prop="telephone_mobile">
                <el-input :value="form.telephone_mobile" @input="update('telephone_mobile', $event)" />
            </el-form-item>
            <el-form-item label="Location phone" prop="telephone_location">
                <el-input :value="form.telephone_location" @input="update('telephone_location', $event)" />
            </el-form-item>
        </div>
        <el-form-item label="Function" prop="function">
            <v-select
                :value="form.function || null"
                :options="functionOptions"
                :clearable="true"
                placeholder="—"
                @input="update('function', $event)"
            />
        </el-form-item>
        <el-form-item label="Language" prop="Language">
            <v-select
                :value="form.Language || null"
                :options="languageOptions"
                :reduce="languageValue"
                label="label"
                :clearable="true"
                placeholder="—"
                @input="update('Language', $event || '')"
            />
            <p class="contact-form-hint">Language used for emails to this contact.</p>
        </el-form-item>
        <el-form-item prop="receive_monitoring">
            <el-checkbox
                :value="form.receive_monitoring"
                :disabled="monitoringLocked"
                @change="update('receive_monitoring', $event)"
            >
                Receive monitoring emails
            </el-checkbox>
            <p class="contact-form-hint">
                Gets alerts when critical X-Cube problems occur at a linked location. A location
                may have multiple monitoring contacts. You cannot turn this off if it would leave
                that location with none.
            </p>
            <p v-if="monitoringLocked" class="contact-form-hint">
                This is the only monitoring contact left for at least one location.
            </p>
        </el-form-item>
        <el-form-item label="Locations" prop="locationIds">
            <el-checkbox-group :value="form.locationIds" @input="update('locationIds', $event)">
                <el-checkbox v-for="loc in locations" :key="loc.id" :label="loc.id">
                    {{ loc.name }}
                </el-checkbox>
            </el-checkbox-group>
            <p v-if="locations.length === 0" class="contact-form-hint">No locations in your account.</p>
        </el-form-item>
    </el-form>
</template>

<script setup>
import { computed, ref } from "vue";
import { buildContactFormRules } from "../../helpers/contactsFormValidation";

const props = defineProps({
    form: { type: Object, required: true },
    locations: { type: Array, default: () => [] },
    functionOptions: { type: Array, default: () => [] },
    languageOptions: { type: Array, default: () => [] },
    canUnsetMonitoring: { type: Boolean, default: true },
});

const emit = defineEmits(["update:form"]);

const formRef = ref(null);
const rules = buildContactFormRules(() => props.form);

const monitoringLocked = computed(
    () => props.form.receive_monitoring && !props.canUnsetMonitoring,
);

const languageValue = (opt) => opt.value;

const update = (key, value) => {
    emit("update:form", { ...props.form, [key]: value });
};

defineExpose({
    validate: () => formRef.value?.validate(),
    clearValidate: () => formRef.value?.clearValidate(),
});
</script>

<style scoped lang="scss">
@use "../../colors" as *;

.contact-form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 1rem;
}

.contact-form-hint {
    margin: 0.25rem 0 0;
    font-size: 0.85rem;
    color: $color-text-primary;
}

.el-checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    max-height: 12rem;
    overflow-y: auto;
}

@media (max-width: 700px) {
    .contact-form-row {
        grid-template-columns: 1fr;
    }
}
</style>
